import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchRosterPlayerGameLog, fetchSeasons } from '../services/league'
import SeasonPicker from '../components/SeasonPicker'
import { TeamHelmet } from '../components/TeamHelmet'
import {
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  ResultBadge,
  RowLink,
  RowLinkTarget,
  SortableHeaderRow,
  Stat,
  dateSortValue,
  formatDate,
  inGamePlayerLogPath,
  playerProfilePath,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import { resolveTecmoTeam } from '../constants/teams'
import {
  ROSTER_STAT_CATEGORIES,
  formatRosterStatValue,
  type RosterStatCategory,
} from '../lib/statCategories'
import type { RosterPlayerGameLog, RosterPlayerGameLogEntry } from '../types'
import './InGamePlayerLog.css'

/** Leading columns before the statistics: date, season, controller, opponent, score. */
const LEADING_COLUMN_COUNT = 5

/**
 * The categories worth a column group for this player.
 *
 * @remarks
 * Driven by each category's own `qualifies` test — the same predicate that
 * decides whether he appears on that leaderboard — so a running back's log
 * carries four rushing columns rather than those plus eight empty passing ones.
 * A player who genuinely did two things, a halfback who threw an option pass,
 * gets both groups.
 */
function relevantCategories(log: RosterPlayerGameLog): RosterStatCategory[] {
  return ROSTER_STAT_CATEGORIES.filter(category => category.qualifies(log.totals))
}

/**
 * The headline totals, which are the leaderboard row this page was opened from.
 *
 * @remarks
 * Rate columns are dropped from the tiles. Over one game a rate is noise, and
 * over a season it is already the last column of the table below; the tiles are
 * for the volume figures a visitor came to check.
 */
function TotalTiles({
  log,
  categories,
}: {
  log: RosterPlayerGameLog
  categories: RosterStatCategory[]
}) {
  const scope = log.seasonNumber === null ? 'All seasons' : `Season ${log.seasonNumber}`

  return (
    <div className="grid-3">
      <Stat
        label="Games"
        value={log.games.length}
        hint={`${scope} · ${log.teamAbbreviation} played ${log.teamGames}`}
      />
      {categories.flatMap(category =>
        category.columns
          .filter(column => !column.format)
          .map(column => (
            <Stat
              key={`${category.slug}-${String(column.key)}`}
              label={`${category.title} · ${column.label}`}
              value={formatRosterStatValue(column, log.totals)}
            />
          ))
      )}
    </div>
  )
}

/**
 * One row per game, with the league member who produced it.
 *
 * @remarks
 * Every row's whole surface opens that game's box score, and the score cell
 * carries the same destination as a real anchor for keyboard users — the
 * {@link RowLink} pattern. The two player names inside point somewhere else
 * entirely, at those members' profiles, which is exactly the case that pattern
 * exists to hold.
 */
function GameLogTable({
  log,
  categories,
}: {
  log: RosterPlayerGameLog
  categories: RosterStatCategory[]
}) {
  const statColumns = useMemo(
    () => categories.flatMap(category => category.columns),
    [categories]
  )

  const columns = useMemo<Array<SortableColumn<RosterPlayerGameLogEntry>>>(
    () => [
      { key: 'date', label: 'Date', sort: game => dateSortValue(game.playedAt) },
      { key: 'season', label: 'Season', className: 'num', sort: game => game.seasonNumber },
      {
        key: 'controller',
        label: 'Controlled by',
        hint: 'The league member using this team in that game',
        sort: game => game.controller.displayName,
      },
      { key: 'opponent', label: 'Opponent', sort: game => game.opponent.displayName },
      {
        key: 'score',
        label: 'Result',
        className: 'num',
        hint: "Sort by the controlling member's margin",
        sortLabel: 'margin',
        sort: game => game.score - game.opponentScore,
      },
      ...statColumns.map(column => ({
        key: String(column.key),
        label: column.label,
        className: 'num',
        sort: (game: RosterPlayerGameLogEntry) => Number(game.stats[column.key]),
      })),
      { key: 'actions' },
    ],
    [statColumns]
  )

  const sort = useTableSort(log.games, columns, { key: 'date', direction: 'desc' })

  return (
    <div className="table-wrap">
      <table className="data game-log">
        <thead>
          {/* Passing and rushing both label a column `Att`, `Yds` and `TD`. The
              leaderboards never show two categories at once and so never need
              this; a log of a quarterback who also runs does. */}
          {categories.length > 1 && (
            <tr className="game-log__groups">
              <th colSpan={LEADING_COLUMN_COUNT} />
              {categories.map(category => (
                <th key={category.slug} colSpan={category.columns.length}>
                  {category.title}
                </th>
              ))}
              <th />
            </tr>
          )}
          <SortableHeaderRow columns={columns} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(game => {
            const boxScorePath = `/games/${game.gameId}`

            return (
              <RowLink key={`${game.gameId}-${game.side}`} to={boxScorePath}>
                <td className="muted game-log__date">{formatDate(game.playedAt)}</td>
                <td className="num">{game.seasonNumber}</td>
                <td>
                  <PlayerCell
                    name={game.controller.displayName}
                    discordId={game.controller.discordId}
                    avatar={game.controller.discordAvatar}
                    to={playerProfilePath(game.controller.leaguePlayerId)}
                  />
                </td>
                <td>
                  <PlayerCell
                    name={game.opponent.displayName}
                    discordId={game.opponent.discordId}
                    avatar={game.opponent.discordAvatar}
                    team={game.opponent.teamAbbreviation}
                    to={playerProfilePath(game.opponent.leaguePlayerId)}
                  />
                </td>
                <td className="num">
                  <RowLinkTarget
                    to={boxScorePath}
                    label={`Box score: ${game.controller.displayName} ${game.score}–${game.opponentScore} ${game.opponent.displayName}`}
                  >
                    <span className="game-log__score">
                      <ResultBadge result={game.result} />
                      <strong>
                        {game.score}–{game.opponentScore}
                      </strong>
                    </span>
                  </RowLinkTarget>
                </td>
                {statColumns.map(column => (
                  <td key={String(column.key)} className="num">
                    {formatRosterStatValue(column, game.stats)}
                  </td>
                ))}
                <td className="num">
                  <Link className="btn btn--ghost btn--sm" to={boxScorePath}>
                    Box score
                  </Link>
                </td>
              </RowLink>
            )
          })}
        </tbody>
        {/* Spells out that the rows add up to the leaderboard row that sent the
            visitor here, which is the one claim this page has to make good on. */}
        <tfoot>
          <tr>
            <td colSpan={LEADING_COLUMN_COUNT}>
              Totals · {log.games.length} {log.games.length === 1 ? 'game' : 'games'}
            </td>
            {statColumns.map(column => (
              <td key={String(column.key)} className="num">
                {formatRosterStatValue(column, log.totals)}
              </td>
            ))}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/**
 * Reads the `season` query parameter.
 *
 * @returns The requested season, `'all'`, or null when the URL asks for nothing
 * usable and the current season should win instead.
 *
 * @remarks
 * The same reading `GamesList` applies, deliberately: a visitor moving between
 * a leaderboard, a game log and a results list should not find `?season=33`
 * meaning three different things.
 */
function parseSeasonParam(value: string | null): number | 'all' | null {
  if (value === 'all') return 'all'
  const parsed = Number(value)
  return value !== null && Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

/**
 * One in-game player's game log — every game an NES athlete produced statistics
 * in, and which league member was controlling him.
 *
 * @remarks
 * Reached by clicking a row on the Leaderboards page's in-game section, whose
 * totals pool every member who used that team. This page is where that pooling
 * comes apart again: one row per game, naming the member behind each line.
 *
 * The player is addressed by team *and* name, both in the URL and in the
 * aggregation behind it, because rosters are team-specific — see
 * {@link inGamePlayerLogPath}. Nothing here is keyed on the slot: two seasons
 * of roster edits can move a name between slots, and a log that split in half
 * when it did would be worse than useless.
 *
 * Season lives in the query string rather than in component state, so a link
 * from a season leaderboard opens that season's log rather than reinterpreting
 * itself as the current one.
 */
export default function InGamePlayerLog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const team = searchParams.get('team')?.trim() ?? ''
  const name = searchParams.get('name')?.trim() ?? ''

  const seasons = useAsync(fetchSeasons, [])
  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonValue = parseSeasonParam(searchParams.get('season')) ?? currentSeason?.number

  const log = useAsync(
    () =>
      team && name
        ? fetchRosterPlayerGameLog(team, name, seasonValue)
        : Promise.resolve(null),
    [team, name, seasonValue]
  )

  function selectSeason(next: number | 'all') {
    const params = new URLSearchParams(searchParams)
    params.set('season', String(next))
    setSearchParams(params, { replace: true })
  }

  if (!team || !name) {
    return (
      <Notice tone="error">
        A game log needs both a team and an in-game player&rsquo;s name.{' '}
        <Link to="/stats" style={{ textDecoration: 'underline' }}>
          Back to the leaderboards
        </Link>
      </Notice>
    )
  }

  const data = log.data
  const categories = data ? relevantCategories(data) : []
  const franchise = resolveTecmoTeam(data?.teamAbbreviation ?? team)
  const allSeasonsPath = inGamePlayerLogPath(team, name, 'all')
  const scopedToSeason = seasonValue !== undefined && seasonValue !== 'all'

  return (
    <div className="stack">
      <header className="page-header">
        <div className="ingame-identity">
          <TeamHelmet abbreviation={data?.teamAbbreviation ?? team} size="lg" />
          <div>
            <span className="eyebrow">In-game player</span>
            <h1>{data?.name ?? name}</h1>
            <p className="muted">
              {[data?.position, franchise?.name ?? team, data?.slot]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </div>
        {seasons.data && seasons.data.length > 0 && (
          <SeasonPicker
            seasons={seasons.data}
            value={seasonValue ?? 'all'}
            onChange={selectSeason}
            includeAll
          />
        )}
      </header>

      <Notice>
        {/* One element: a Notice lays its children out as flex items, so a bare
            second span would break onto a column of its own. */}
        <span>
          These are the Tecmo roster player&rsquo;s own numbers, pooled across
          everyone who used {franchise?.name ?? team}. The{' '}
          <strong>Controlled by</strong> column names the league member behind
          each line — follow it for their profile, or open a row for the full
          box score.
        </span>
      </Notice>

      {log.error && <Notice tone="error">{log.error}</Notice>}

      {log.loading || !data ? (
        <Card>
          <Loading rows={6} />
        </Card>
      ) : data.games.length === 0 ? (
        <Card>
          <EmptyState
            title={`Nothing on record for ${data.name}${
              scopedToSeason ? ` in season ${seasonValue}` : ''
            }`}
          >
            In-game player stats come from Tecmo Super Bowl save states, which the
            league started uploading in season 33.{' '}
            {scopedToSeason && (
              <Link to={allSeasonsPath} style={{ textDecoration: 'underline' }}>
                Try all seasons
              </Link>
            )}
          </EmptyState>
        </Card>
      ) : (
        <>
          <TotalTiles log={data} categories={categories} />

          <Card
            title={`${data.games.length} ${data.games.length === 1 ? 'game' : 'games'}`}
            subtitle="Newest first. Click any column heading to re-sort."
            flush
          >
            <GameLogTable log={data} categories={categories} />
          </Card>

          <p className="muted" style={{ fontSize: '0.82rem' }}>
            {data.teamAbbreviation} was used in {data.teamGames} game
            {data.teamGames === 1 ? '' : 's'} with a box score in this scope, and{' '}
            {data.name} recorded something in {data.games.length} of them. The totals
            row is the same figure the{' '}
            <Link to="/stats" style={{ textDecoration: 'underline' }}>
              leaderboard
            </Link>{' '}
            shows for this scope.
          </p>
        </>
      )}
    </div>
  )
}
